<script setup lang="ts">
import type { ReceivableRow } from '~/ui-blocks/Formalization/blocks/paymentModel/receivables/useReceivableValues'
import type { ReceiptModel, ReceiptTransfer } from '~/utils/receipt-model/ReceiptModel'
import { SolDivider, SolEmptyState } from '@solfacil/girassol'
import IconExpandLess from '~icons/material-symbols/expand-less'
import IconExpandMore from '~icons/material-symbols/expand-more'
import IconMonetizationOn from '~icons/material-symbols/monetization-on'
import useDownloadReceipt from '~/store-v2/payment-model/useDownloadReceipt'
import FinancingDetails from '~/ui-blocks/Formalization/blocks/paymentModel/receivables/FinancingDetails.vue'
import TransferRow from '~/ui-blocks/Formalization/blocks/paymentModel/receivables/TransferRow.vue'
import { useReceivableValues } from '~/ui-blocks/Formalization/blocks/paymentModel/receivables/useReceivableValues'

const props = withDefaults(defineProps<{
  data: ReceiptModel
  stepStatus?: string | null
  supplierName?: string | null
  isReimbursable?: boolean
  projectId?: string | null
  expectsInvoices?: boolean
}>(), {
  stepStatus: null,
  supplierName: null,
  isReimbursable: false,
  projectId: null,
  expectsInvoices: true,
})

const { t } = useI18n()
const { formatMoney } = useMoney()

const {
  state,
  groups,
  total,
  totalDisplay,
  financingDetails,
} = useReceivableValues(() => props.data, {
  stepStatus: () => props.stepStatus,
  supplierName: () => props.supplierName,
  isReimbursable: () => props.isReimbursable,
})

type PanelSection = 'values' | 'details'

const openSection = ref<PanelSection | null>('values')
const isValuesOpen = computed(() => openSection.value === 'values')
const isDetailsOpen = computed(() => openSection.value === 'details')

function toggleSection(section: PanelSection) {
  openSection.value = openSection.value === section ? null : section
}

const baseId = useId()
const ids = {
  valuesToggle: `${baseId}-values-toggle`,
  valuesPanel: `${baseId}-values-panel`,
  detailsToggle: `${baseId}-details-toggle`,
  detailsPanel: `${baseId}-details-panel`,
  emptyState: `${baseId}-receivables-empty`,
  noValuesState: `${baseId}-receivables-no-values`,
}

function getGroupLabelId(groupKey: string) {
  return `${baseId}-group-${groupKey}`
}

function getRowTitle(row: ReceivableRow) {
  return t('receipt-model.receivables.transfer_title', { number: row.number })
}

function getRowDescription(row: ReceivableRow) {
  return row.descriptionKey ? t(row.descriptionKey, row.descriptionParams) : ''
}

const receiptProjectId = computed(() => props.projectId || props.data.project_id || '')
const { mutateAsync: downloadReceipt } = useDownloadReceipt(receiptProjectId)
const downloadingIds = ref<Set<string>>(new Set())

async function downloadTransferReceipt(transfer: ReceiptTransfer) {
  if (downloadingIds.value.has(transfer.id))
    return

  downloadingIds.value.add(transfer.id)
  try {
    await downloadReceipt(transfer)
  }
  catch {
    // `useDownloadReceipt` already shows the error toast.
  }
  finally {
    downloadingIds.value.delete(transfer.id)
  }
}

function handleDownload(row: ReceivableRow) {
  return downloadTransferReceipt(row.transfer)
}

const isDownloadingKitReceipt = computed(() => {
  const transferId = financingDetails.value.equipment?.receiptTransfer?.id
  return !!transferId && downloadingIds.value.has(transferId)
})
</script>

<template>
  <section class="receivable-values" data-testid="receivable-values">
    <h3>
      <button
        :id="ids.valuesToggle"
        type="button"
        class="receivable-values__toggle"
        :class="isValuesOpen ? 'receivable-values__toggle--open' : 'receivable-values__toggle--closed'"
        :aria-expanded="isValuesOpen"
        :aria-controls="ids.valuesPanel"
        @click="toggleSection('values')"
      >
        <span class="receivable-values__toggle-title">
          {{ t('receipt-model.receivables.title') }}
        </span>
        <IconExpandLess v-if="isValuesOpen" class="receivable-values__toggle-icon" aria-hidden="true" />
        <IconExpandMore v-else class="receivable-values__toggle-icon" aria-hidden="true" />
      </button>
    </h3>

    <div
      v-show="isValuesOpen"
      :id="ids.valuesPanel"
      role="region"
      :aria-labelledby="ids.valuesToggle"
      class="p-2xs bg-neutral-high-pure"
    >
      <div v-if="state === 'transfers'" class="flex flex-col gap-5">
        <template v-for="group in groups" :key="group.key">
          <section class="flex flex-col gap-4" :aria-labelledby="getGroupLabelId(group.key)">
            <h4 :id="getGroupLabelId(group.key)" class="receivable-values__group-label">
              {{ t(group.labelKey) }}
            </h4>

            <ul role="list" class="flex flex-col gap-4">
              <li v-for="row in group.rows" :key="`${row.id}-${row.number}`">
                <TransferRow
                  :title="getRowTitle(row)"
                  :description="getRowDescription(row)"
                  :value="row.value"
                  :status="row.status"
                  :can-download-receipt="row.canDownloadReceipt"
                  :downloading="downloadingIds.has(row.id)"
                  @download="handleDownload(row)"
                />
              </li>
            </ul>
          </section>

          <SolDivider thickness="x-small" orientation="horizontal" />
        </template>

        <div class="flex items-center justify-between gap-2">
          <span class="fonts-body-large-bold text-neutral-low-dark">
            {{ t('receipt-model.receivables.total') }}
          </span>

          <span v-if="totalDisplay === 'analysis'" class="receivable-values__total-value text-brand-primary-light">
            {{ t('receipt-model.receivables.in_analysis') }}
          </span>
          <s v-else-if="totalDisplay === 'struck'" class="receivable-values__total-value line-through text-brand-primary-light">
            {{ formatMoney(total) }}
          </s>
          <span v-else class="receivable-values__total-value text-neutral-low-dark">
            {{ formatMoney(total) }}
          </span>
        </div>
      </div>

      <div v-else-if="state === 'empty'" class="flex flex-col gap-6">
        <SolEmptyState
          :id="ids.emptyState"
          class="receivable-values__empty-state pb-4xs"
          variant="period-payment"
          :title="t(expectsInvoices ? 'receipt-model.receivables.empty.title' : 'receipt-model.receivables.empty_confirm.title')"
          :subtitle="t(expectsInvoices ? 'receipt-model.receivables.empty.subtitle' : 'receipt-model.receivables.empty_confirm.subtitle')"
        />

        <SolDivider thickness="x-small" orientation="horizontal" />

        <div class="flex items-center justify-between gap-2">
          <span class="flex items-center gap-1 text-neutral-low-light">
            <IconMonetizationOn class="icon-size-medium shrink-0" aria-hidden="true" />
            <span class="fonts-body-large-bold">
              {{ t('receipt-model.receivables.total') }}
            </span>
          </span>
          <span class="fonts-body-medium-bold shrink-0 text-brand-primary-light">-</span>
        </div>
      </div>

      <SolEmptyState
        v-else
        :id="ids.noValuesState"
        class="receivable-values__empty-state receivable-values__empty-state--no-values pb-4xs"
        variant="empty-folder"
        :title="t('receipt-model.receivables.no_values.title')"
        :subtitle="t('receipt-model.receivables.no_values.subtitle')"
      />
    </div>

    <h3>
      <button
        :id="ids.detailsToggle"
        type="button"
        class="receivable-values__toggle border-t-xs"
        :class="isDetailsOpen ? 'receivable-values__toggle--open' : 'receivable-values__toggle--closed'"
        :aria-expanded="isDetailsOpen"
        :aria-controls="ids.detailsPanel"
        @click="toggleSection('details')"
      >
        <span class="receivable-values__toggle-title">
          {{ t('receipt-model.receivables.details_title') }}
        </span>
        <IconExpandLess v-if="isDetailsOpen" class="receivable-values__toggle-icon" aria-hidden="true" />
        <IconExpandMore v-else class="receivable-values__toggle-icon" aria-hidden="true" />
      </button>
    </h3>

    <FinancingDetails
      v-show="isDetailsOpen"
      :id="ids.detailsPanel"
      role="region"
      :aria-labelledby="ids.detailsToggle"
      :details="financingDetails"
      :downloading-kit-receipt="isDownloadingKitReceipt"
      @download-receipt="downloadTransferReceipt"
    />
  </section>
</template>

<style scoped lang="scss">
.receivable-values {
  @apply w-full overflow-hidden rounded-lg border-1 border-neutral-high-medium bg-neutral-high-pure;
}

.receivable-values__toggle {
  @apply flex w-full items-center justify-between gap-4 pt-4xs pb-3xs px-2xs text-left;
  @apply border-neutral-high-medium;

  // Inset ring: the container's `overflow-hidden` would clip an outer one.
  &:focus {
    @apply outline-none;
  }

  &:focus-visible {
    @apply ring-2 ring-inset ring-highlight-pure;
  }
}

.receivable-values__toggle--open {
  @apply bg-neutral-high-pure border-b-xs;
}

.receivable-values__toggle--closed {
  @apply bg-neutral-high-light;
}

// Fixed 20px: `fonts-heading-h3` grows to 24px from 1024px.
.receivable-values__toggle-title {
  @apply font-highlight font-regular text-xs leading-xs text-neutral-low-dark;
}

.receivable-values__toggle-icon {
  @apply icon-size-medium shrink-0 text-neutral-low-dark;
}

// Figma Overline Small (Lato Bold 10): the preset's `fonts-overline-small` is 16px regular.
.receivable-values__group-label {
  @apply font-base font-bold text-[10px] leading-xs tracking-md uppercase text-neutral-low-light;
}

.receivable-values__total-value {
  @apply fonts-body-large-bold shrink-0 whitespace-nowrap;
}

// SolEmptyState overridden to the Figma: 115px illustration (no token) and Lato 16 bold / 14 regular texts.
.receivable-values__empty-state {
  :deep(.icon) {
    @apply w-[115px] h-[115px] mb-quark;
  }

  :deep(.icon img) {
    @apply w-full h-full;
  }

  &--no-values :deep(.icon) {
    @apply mb-micro;
  }

  :deep(.title) {
    @apply fonts-body-large-bold text-brand-primary-light;
  }

  :deep(.subtitle) {
    @apply fonts-body-medium-regular text-brand-primary-light;
  }
}
</style>
